import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { memberApi } from "../../lib/api";
import { useMemberAuth } from "../../context/MemberAuthContext";
import MemberLayout, { Avatar } from "../../components/MemberLayout";
import { toast } from "../../hooks/use-toast";
import { Search, Filter, MapPin, MessageSquare } from "lucide-react";

const Directory = () => {
  const { member, updateProfile } = useMemberAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [skill, setSkill] = useState("");
  const [data, setData] = useState({ members: [], facets: { industries: [], skills: [] } });
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!member) return;
    if (!member.searchable) { setBlocked(true); setLoading(false); return; }
    setBlocked(false);
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await memberApi.get('/member/directory', { params: { q: q || undefined, industry: industry || undefined, skill: skill || undefined } });
        setData(data);
      } catch (err) {
        if (err?.response?.status === 403) setBlocked(true);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, industry, skill, member]);

  const optBackIn = async () => {
    try { await updateProfile({ searchable: true }); toast({ title: 'Welcome back to the directory' }); setBlocked(false); }
    catch { toast({ title: 'Could not opt in' }); }
  };

  if (blocked) {
    return (
      <MemberLayout>
        <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · Directory</div>
        <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-4">Directory <span className="font-serif-i text-[#e0684d]">access</span></h1>
        <div className="rounded-lg p-6" style={{ background: '#fbf8ef' }}>
          <p className="text-[15px] text-[#0d2a20]/85 mb-4 max-w-[640px]">You currently don't have access to the Member Directory because you've opted out of appearing in it. The directory is reciprocal — to see other members, you also need to be visible to them.</p>
          <button onClick={optBackIn} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px]">Opt back in to the directory</button>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout>
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · Directory</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-6">The <span className="font-serif-i text-[#e0684d]">network</span></h1>

      <div className="grid md:grid-cols-[1fr_220px_220px] gap-3 mb-6">
        <div className="relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0d2a20]/40" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, skill, industry, company…" className="w-full pl-10 pr-3 py-2.5 rounded-md bg-white border text-[14px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
        <select value={industry} onChange={e => setIndustry(e.target.value)} className="px-3 py-2.5 rounded-md bg-white border text-[14px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
          <option value="">All industries</option>
          {data.facets.industries.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={skill} onChange={e => setSkill(e.target.value)} className="px-3 py-2.5 rounded-md bg-white border text-[14px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
          <option value="">All skills</option>
          {data.facets.skills.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="rounded-lg p-10 text-center text-[14px] text-[#0d2a20]/60" style={{ background: '#fbf8ef' }}>Loading members…</div>
      ) : data.members.length === 0 ? (
        <div className="rounded-lg p-10 text-center text-[14px] text-[#0d2a20]/60" style={{ background: '#fbf8ef' }}>No members match your search yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.members.map(m => (
            <Link key={m.id} to={`/member/directory/${m.id}`} className="rounded-lg p-5 org-card block" style={{ background: '#fbf8ef' }}>
              <div className="flex items-start gap-3 mb-3">
                <Avatar src={m.avatar_url} name={m.display_name} size={48} />
                <div className="min-w-0">
                  <div className="font-medium text-[15px] truncate">{m.display_name || 'Member'}</div>
                  <div className="text-[12px] text-[#0d2a20]/70 truncate">{m.headline || '—'}</div>
                  {m.company && <div className="text-[12px] text-[#0d2a20]/55 truncate">{m.company}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#0d2a20]/60 mb-3 flex-wrap">
                {m.industry && <span className="px-2 py-0.5 rounded" style={{ background: '#ece5d3' }}>{m.industry}</span>}
                {m.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {m.location}</span>}
              </div>
              {m.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {m.skills.slice(0, 4).map(s => <span key={s} className="px-2 py-0.5 rounded text-[11px]" style={{ background: '#f4efe4', color: '#0d2a20' }}>{s}</span>)}
                  {m.skills.length > 4 && <span className="text-[11px] text-[#0d2a20]/55">+{m.skills.length - 4}</span>}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </MemberLayout>
  );
};

export default Directory;
